import { Component, OnInit, ViewChild, signal, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageService, ConfirmationService, SortMeta } from 'primeng/api';
import { TipoMaterialService } from '../../services/tipo-material/tipo-material.service';
import { DialogService } from '../../shared/services/dialog.service';
import { TipoMaterial } from '../../domain/tipo-material.domain';
import { environment } from '../../../environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { HttpParams } from '@angular/common/http';

interface Column {
  field: string;
  header: string;
  fieldType?: 'text' | 'boolean' | 'boolean-icon' | 'number' | 'date';
  sortable?: boolean;
  exportable?: boolean;
  filtrable?: boolean;
  modeDisplayFilter?: 'menu' | 'row';
}

@Component({
  selector: 'app-tipos-material',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    RippleModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService],
  templateUrl: './tipos-material.component.html',
  styleUrls: ['./tipos-material.component.scss']
})
export class TiposMaterialComponent implements OnInit, OnDestroy {
  @ViewChild('dt') table: Table | undefined;

  // Signals para estado reactive
  tiposMaterial = signal<TipoMaterial[]>([]);
  loading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  selectedRows: TipoMaterial[] = [];
  filterValue: string = '';

  // Configuración
  nroFilasPorPagina: number = environment.REGISTROS_PER_PAGE || 10;
  paginaActual = 0;
  paginate = true;
  rowsPerPageOptions: number[] = [];

  // Control de suscripciones y reintentos
  private destroy$ = new Subject<void>();
  private errorCount = 0;
  private readonly MAX_ERROR_COUNT = 3;
  private lastErrorTimestamp = 0;
  private readonly ERROR_RESET_TIMEOUT = 60000; // 1 minuto

  // Columnas de la tabla
  columnsTable: Column[] = [
    { field: 'codigo', header: 'Código', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'nombre', header: 'Nombre', fieldType: 'text', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'factorPrecio', header: 'Factor Precio', fieldType: 'number', sortable: true, exportable: true, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'aplicaATanda', header: 'Aplica a Tanda', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'esNoTradicional', header: 'No Tradicional', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'active', header: 'Activo', fieldType: 'boolean-icon', sortable: true, exportable: false, filtrable: true, modeDisplayFilter: 'menu' },
    { field: 'accion', header: 'Acciones', sortable: false, filtrable: false }
  ];

  // Para filtros y ordenamiento
  multiSortMeta: SortMeta[] = [];
  filtersMetadata: any = {};

  // Helper para limpiar tabla
  clearTableInProgress = false;
  clearTableCountLazyExecuted = 0;

  constructor(
    private tipoMaterialService: TipoMaterialService,
    private router: Router,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService
  ) {
    if (this.rowsPerPageOptions.length === 0) {
      this.rowsPerPageOptions = environment.REGISTROS_PER_PAGE_OPTIONS || [10, 20, 30, 50];
    }
  }

  ngOnInit(): void {
    this.loading.set(false);
    this.clearTableInProgress = false;
    this.clearTableCountLazyExecuted = 0;
    this.errorCount = 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLazy(event: TableLazyLoadEvent): void {
    // Manejo del bug de bucle infinito al hacer clear
    if (this.clearTableInProgress && this.clearTableCountLazyExecuted === 0) {
      this.clearTableCountLazyExecuted++;
      if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
        this.paginaActual = Math.floor(event.first / event.rows) + 1;
      } else {
        this.paginaActual = 1;
      }
      this.processLazyEvent(event);
      this.cargar();
    } else if (this.clearTableInProgress) {
      return;
    }

    this.processLazyEvent(event);
    this.cargar();
  }

  private processLazyEvent(event: TableLazyLoadEvent): void {
    if (event.first !== undefined && event.rows !== null && event.rows !== undefined) {
      // API Platform usa páginas basadas en 1, PrimeNG usa índice basado en 0
      this.paginaActual = Math.floor(event.first / event.rows) + 1;
      this.nroFilasPorPagina = event.rows;
    } else {
      this.paginaActual = 1;
      this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE ?? 10;
    }
    this.multiSortMeta = event.multiSortMeta || [];
    this.filterValue = typeof event.globalFilter === 'string' ? event.globalFilter : '';
    this.filtersMetadata = event.filters || {};
  }

  getFilterType(col: Column): string {
    if (!col.fieldType) return 'text';
    if (col.fieldType === 'date') return 'date';
    if (col.fieldType === 'boolean' || col.fieldType === 'boolean-icon') return 'boolean';
    if (col.fieldType === 'number') return 'numeric';
    return 'text';
  }

  getMatchMode(fieldType?: string): string {
    if (fieldType === 'boolean') return 'equals';
    if (fieldType === 'number') return 'equals';
    return 'contains';  // Para texto
  }

  cargar(): void {
    // Verificar si hemos excedido el límite de errores
    const now = Date.now();
    if (now - this.lastErrorTimestamp > this.ERROR_RESET_TIMEOUT) {
      this.errorCount = 0;
    }

    if (this.errorCount >= this.MAX_ERROR_COUNT) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Se alcanzó el límite de reintentos (${this.MAX_ERROR_COUNT}). Por favor, recarga la página o contacta al administrador.`,
        life: 8000,
        sticky: true
      });
      return;
    }

    this.loading.set(true);

    this.tipoMaterialService.findAll(
      this.paginate,
      this.paginaActual,
      this.nroFilasPorPagina,
      this.filterValue || '',
      this.multiSortMeta,
      this.filtersMetadata
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: any) => {
          this.loading.set(false);
          this.errorCount = 0;
          this.lastErrorTimestamp = 0;

          const members = resp['hydra:member'] || resp.member || resp;

          const tiposMaterialMapeados = members.map((tipoMaterial: any) => ({
            id: tipoMaterial.id,
            codigo: tipoMaterial.codigo,
            nombre: tipoMaterial.nombre,
            factorPrecio: tipoMaterial.factorPrecio,
            aplicaATanda: tipoMaterial.aplicaATanda ?? false,
            esNoTradicional: tipoMaterial.esNoTradicional ?? false,
            active: tipoMaterial.active ?? true
          }));

          this.tiposMaterial.set(tiposMaterialMapeados);
          this.totalRecords.set(resp['hydra:totalItems'] || resp.totalItems || tiposMaterialMapeados.length);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;
        },
        error: (error: any) => {
          this.loading.set(false);
          this.clearTableInProgress = false;
          this.clearTableCountLazyExecuted = 0;

          this.errorCount++;
          this.lastErrorTimestamp = Date.now();

          console.error(`Error al recuperar tipos de material (intento ${this.errorCount}/${this.MAX_ERROR_COUNT}):`, error);

          const errorDetail = this.errorCount >= this.MAX_ERROR_COUNT
            ? 'Se alcanzó el límite de reintentos. Por favor, recarga la página.'
            : `Ocurrió un error al recuperar tipos de material (intento ${this.errorCount}/${this.MAX_ERROR_COUNT})`;

          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorDetail,
            life: this.errorCount >= this.MAX_ERROR_COUNT ? 8000 : 4000,
            sticky: this.errorCount >= this.MAX_ERROR_COUNT
          });

          this.filtersMetadata = {};
          this.multiSortMeta = [];
          this.tiposMaterial.set([]);
          this.totalRecords.set(0);
        }
      });
  }

  clear(table: Table): void {
    this.paginate = true;
    this.paginaActual = 0;
    this.nroFilasPorPagina = environment.REGISTROS_PER_PAGE || 10;
    this.clearTableInProgress = true;
    this.filterValue = '';
    this.selectedRows = [];
    this.filtersMetadata = {};
    this.multiSortMeta = [];
    this.errorCount = 0;
    this.lastErrorTimestamp = 0;
    table.clear();
  }

  toggleTipoMaterialActivo(tipoMaterial: TipoMaterial): void {
    if (!tipoMaterial.id) return;

    const nuevoEstado = tipoMaterial.active ?? true;

    const tipoMaterialActualizado: TipoMaterial = {
      active: nuevoEstado
    };

    this.loading.set(true);
    this.tipoMaterialService.patch(tipoMaterial.id, tipoMaterialActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp: TipoMaterial) => {
          this.loading.set(false);
          const estadoFinal = resp.active ?? false;
          const accionTxt = estadoFinal ? 'activado' : 'desactivado';
          this.messageService.add({
            severity: 'success',
            summary: `Tipo de Material ${accionTxt}`,
            detail: `El tipo de material ha sido ${accionTxt} correctamente.`,
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          // Revertir el cambio en caso de error
          tipoMaterial.active = !nuevoEstado;
          console.error('Error al actualizar tipo de material:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo actualizar el estado del tipo de material',
            life: 3000
          });
        }
      });
  }

  editarTipoMaterial(id: number): void {
    this.router.navigate(['/tipos-material/edit', id]);
  }

  navigateToCreateTipoMaterial(): void {
    this.router.navigate(['/tipos-material/edit/nuevo']);
  }

  eliminarTipoMaterial(tipoMaterial: TipoMaterial): void {
    this.confirmationService.confirm({
      message: `¿Está seguro que desea desactivar el tipo de material "${tipoMaterial.nombre}"?`,
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (!tipoMaterial.id) return;

        this.loading.set(true);
        const tipoMaterialActualizado: TipoMaterial = {
          active: false
        };

        this.tipoMaterialService.patch(tipoMaterial.id, tipoMaterialActualizado)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loading.set(false);
              this.messageService.add({
                severity: 'success',
                summary: 'Tipo de Material Desactivado',
                detail: 'El tipo de material ha sido desactivado correctamente',
                life: 3000
              });
              this.cargar();
            },
            error: (error) => {
              this.loading.set(false);
              console.error('Error al desactivar tipo de material:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo desactivar el tipo de material',
                life: 3000
              });
            }
          });
      }
    });
  }

  activarTipoMaterial(tipoMaterial: TipoMaterial): void {
    if (!tipoMaterial.id) return;

    this.loading.set(true);
    const tipoMaterialActualizado: TipoMaterial = {
      active: true
    };

    this.tipoMaterialService.patch(tipoMaterial.id, tipoMaterialActualizado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'success',
            summary: 'Tipo de Material Activado',
            detail: 'El tipo de material ha sido activado correctamente',
            life: 3000
          });
          this.cargar();
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al activar tipo de material:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo activar el tipo de material',
            life: 3000
          });
        }
      });
  }
}